import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { Alert, App, Button, Form, Modal, Popconfirm, Segmented, Select, Space, Switch, Table, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, Pill, SectionCard, TableSurface, type Stat } from '../../theme/listkit'
import { GraduationCap, UsersRound, RefreshCw, Sparkles, Image as ImageIcon, XCircle } from '../../components/icons'
import { supabaseClient } from '../../lib/supabaseClient'
import { CLASS_YEAR_OPTIONS } from '../../lib/classYears'
import { groupsAdminErrorMessage } from '../groups/adminErrors'
import {
  MEMORIES_FLAG_KEY,
  useClassroomSettings,
  type ClassroomMode,
  type ClassroomSettings,
} from './useClassroomSettings'
import {
  CLASSROOM_MODES,
  MODE_HELP,
  MODE_LABELS,
  addedYears,
  classroomOpenState,
  isWidening,
  modeConfirm,
  normalizeYears,
  openNowText,
  pilotYearLabel,
  upstreamBlock,
  yearsConfirm,
  yearsInactive,
  yearsWithoutClassroom,
} from './rollout'

/** classroom_settings.updated_at, formatted for the "Last changed" line under the rollout controls. */
const fmt = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

/** Every card is capped: the owner's display is 3440px wide (community_manager/controls/page.tsx). */
const CARD = { maxWidth: 720 } as const
const muted: React.CSSProperties = { fontFamily: brand.fSans, fontWeight: 500, fontSize: 12, color: brand.sub, lineHeight: 1.5 }
const strong: React.CSSProperties = { fontFamily: brand.fSans, fontWeight: 700, fontSize: 13, color: brand.ink }
const fieldLabel: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: brand.gold,
  marginBottom: 6,
}

interface Person {
  user_id: string
  name: string
}

interface ChapterRow {
  id: string
  class_year: string
  name: string
  status: string
  member_count: number
  directory_count: number
  reps: Person[]
  /** Memories in this Classroom, every moderation status (admins read them all). */
  memory_count: number
  /** The Founding Classmate (public.classroom_founders), or null before a classmate has posted. */
  founder: Person | null
  /**
   * False when classroom_founders or photos.group_id could not be read — e.g.
   * the dashboard auto-deployed from master before the Classrooms migrations
   * were applied (docs/classrooms/ROLLOUT.md). The roster still loads; the
   * Memories and Founder cells then show "—" instead of a confident 0 / "None yet".
   */
  extras_known: boolean
}

/**
 * Classrooms — the auto-created "Class of YYYY" chapters (public.groups where
 * kind = 'class_chapter'; the profiles.class_year trigger in
 * 20260930120600_groups_class_chapters.sql keeps membership synced), which the
 * app shows as Classrooms (Classrooms Phase 1). Code keeps the word "chapter";
 * only the copy says Classroom.
 *
 * This page never edits a chapter's name/category/privacy — those are chapter
 * shape decisions the app owns. It owns four things:
 *   - WHO CAN OPEN CLASSROOMS: the public.classroom_settings singleton (mode
 *     off · admins · pilot · everyone, plus pilot_years). can_see_group() ANDs
 *     classroom_open(class_year) into its chapter branch, so this one row gates
 *     every chapter read on the server. Widening asks first; narrowing is one
 *     tap — the brake is never gated (the Community Manager Controls rule).
 *   - THE MEMORIES SWITCH: feature_flags.classrooms_memories_enabled. Turning
 *     it on asks first; off is one tap; a missing or unreadable row disables it
 *     (the Suggestion Box switch pattern).
 *   - CLASS REPS: assign (set_member_role → organizer) and remove
 *     (set_member_role → member). Chapters have no last-organizer rule
 *     (20261016120500_classrooms_reps.sql), so a class's only rep can be
 *     removed; zero reps is a valid state.
 *   - The backfill, for years that predate the sync trigger or a flag flip.
 *
 * "Directory count" is deliberately `profiles` filtered by class_year, NOT
 * `directory_profiles` — the same reasoning as GroupMembersTable: an admin
 * comparing chapter membership against the real class roster needs the true
 * count, not one that silently excludes members who opted out of the public
 * directory.
 *
 * The class name links to /groups/show/:id — that chapter's admin roster.
 * App.useApp().modal only: the static Modal.confirm does not mount under this
 * app's React 19 setup (jaguar_owned_controls/page.tsx).
 */
export function GroupChaptersList() {
  const { message, modal } = App.useApp()
  const {
    settings,
    unreadable,
    loading: settingsLoading,
    refetch: refetchSettings,
    save,
    memoriesEnabled,
    setMemories,
    upstream,
    changedAt,
    changedBy,
  } = useClassroomSettings()
  const block = upstreamBlock(upstream)
  const [rows, setRows] = useState<ChapterRow[]>([])
  const [loading, setLoading] = useState(true)
  const [chaptersKnown, setChaptersKnown] = useState(false)
  const [backfilling, setBackfilling] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingMemories, setSavingMemories] = useState(false)
  const [assignFor, setAssignFor] = useState<ChapterRow | null>(null)
  const [assignOptions, setAssignOptions] = useState<{ value: string; label: string }[]>([])
  const [assignEmpty, setAssignEmpty] = useState('No members in this chapter yet')
  const [assignLoading, setAssignLoading] = useState(false)
  const [assignTo, setAssignTo] = useState<string | undefined>(undefined)
  const [assigning, setAssigning] = useState(false)
  const assignRequestRef = useRef(0)
  const chapterStatus = chaptersKnown ? new Map(rows.map((r) => [r.class_year.trim(), r.status])) : null

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: chapters, error: cErr } = await supabaseClient
        .from('groups')
        .select('id, class_year, name, member_count, status')
        .eq('kind', 'class_chapter')
        .order('class_year', { ascending: false })
      if (cErr) throw cErr
      const chapterRows = (chapters ?? []) as {
        id: string
        class_year: string
        name: string
        member_count: number
        status: string
      }[]
      const ids = chapterRows.map((c) => c.id)

      // Class reps (active organizers), founders and memory counts per chapter.
      let organizers: { group_id: string; user_id: string }[] = []
      let founders: { group_id: string; user_id: string }[] = []
      const memoriesByGroup = new Map<string, number>()
      let extrasKnown = true
      if (ids.length > 0) {
        const [orgRes, founderRes, memoryRes] = await Promise.all([
          supabaseClient
            .from('group_members')
            .select('group_id, user_id')
            .in('group_id', ids)
            .eq('role', 'organizer')
            .eq('status', 'active'),
          supabaseClient.from('classroom_founders').select('group_id, user_id').in('group_id', ids),
          // One select of group_id over every memory, counted client-side
          // (spec B10). Admins read every moderation status, so memories under
          // review count too. PostgREST's max-rows cap bounds this; fine at
          // pilot scale — move to a count view if Classrooms ever hold thousands.
          supabaseClient.from('photos').select('group_id').not('group_id', 'is', null),
        ])
        if (orgRes.error) throw orgRes.error
        organizers = (orgRes.data ?? []) as { group_id: string; user_id: string }[]
        if (founderRes.error || memoryRes.error) {
          extrasKnown = false
        } else {
          founders = (founderRes.data ?? []) as { group_id: string; user_id: string }[]
          for (const p of (memoryRes.data ?? []) as { group_id: string | null }[]) {
            if (p.group_id) memoriesByGroup.set(p.group_id, (memoriesByGroup.get(p.group_id) ?? 0) + 1)
          }
        }
      }

      // Names for reps AND founders in one profiles read, keyed on user_id —
      // never profiles.id, which differs from user_id in every row.
      const nameIds = Array.from(new Set([...organizers, ...founders].map((r) => r.user_id)))
      let nameById = new Map<string, string>()
      if (nameIds.length > 0) {
        const { data: profiles, error: pErr } = await supabaseClient
          .from('profiles')
          .select('user_id, name')
          .in('user_id', nameIds)
        if (pErr) throw pErr
        nameById = new Map(
          (profiles ?? [])
            .filter((p): p is { user_id: string; name: string } => p.user_id != null)
            .map((p) => [p.user_id, p.name || 'Unknown member']),
        )
      }
      const person = (userId: string): Person => ({ user_id: userId, name: nameById.get(userId) ?? 'Unknown member' })
      const repsByGroup = new Map<string, Person[]>()
      for (const o of organizers) {
        const list = repsByGroup.get(o.group_id) ?? []
        list.push(person(o.user_id))
        repsByGroup.set(o.group_id, list)
      }
      const founderByGroup = new Map(founders.map((f) => [f.group_id, person(f.user_id)]))

      // Directory (real roster) counts per class year — one grouped query,
      // counted client-side (the class-year domain is small, ~30 years).
      const { data: profileYears, error: dErr } = await supabaseClient.from('profiles').select('class_year')
      if (dErr) throw dErr
      const countByYear = new Map<string, number>()
      for (const p of (profileYears ?? []) as { class_year: string | null }[]) {
        const y = (p.class_year ?? '').trim()
        if (!y) continue
        countByYear.set(y, (countByYear.get(y) ?? 0) + 1)
      }

      setRows(
        chapterRows.map((c) => ({
          id: c.id,
          class_year: c.class_year,
          name: c.name,
          status: c.status,
          member_count: c.member_count,
          directory_count: countByYear.get(c.class_year) ?? 0,
          reps: repsByGroup.get(c.id) ?? [],
          memory_count: memoriesByGroup.get(c.id) ?? 0,
          founder: founderByGroup.get(c.id) ?? null,
          extras_known: extrasKnown,
        })),
      )
      // A failed load leaves chaptersKnown as it was — never claim we know
      // chapter status from a roster we couldn't actually read.
      setChaptersKnown(true)
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [message])

  useEffect(() => {
    void load()
  }, [load])

  async function backfill() {
    setBackfilling(true)
    try {
      const { data, error } = await supabaseClient.rpc('backfill_class_chapters')
      if (error) throw error
      const n = Number(data ?? 0)
      if (n > 0) {
        message.success(`Added ${n.toLocaleString()} memberships`)
      } else if (upstream.chaptersOn === true) {
        message.success('Nothing to add: every Classroom was already in sync.')
      } else {
        message.warning('Nothing was backfilled. Class chapters may be off in Group Settings; the backfill only runs while it is on.')
      }
      await load()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setBackfilling(false)
    }
  }

  async function openAssign(row: ChapterRow) {
    const requestId = ++assignRequestRef.current
    setAssignFor(row)
    setAssignTo(undefined)
    setAssignOptions([])
    setAssignEmpty('No members in this chapter yet')
    setAssignLoading(true)
    try {
      const { data: members, error: mErr } = await supabaseClient
        .from('group_members')
        .select('user_id')
        .eq('group_id', row.id)
        .eq('status', 'active')
      if (mErr) throw mErr
      const memberIds = (members ?? []).map((m) => m.user_id)
      const repIds = new Set(row.reps.map((rep) => rep.user_id))
      const remainingIds = memberIds.filter((id) => !repIds.has(id))
      // A cancel-then-reopen (dialog opened on chapter A, closed, opened on
      // B) can leave A's read resolving after B's has already started. Never
      // let a stale response write A's members, or A's empty message, over
      // B's — check the request counter right before every set call below.
      if (requestId !== assignRequestRef.current) return
      setAssignEmpty(memberIds.length === 0 ? 'No members in this chapter yet' : 'Everyone in this chapter is already a class rep')
      if (remainingIds.length === 0) return
      const { data: profiles, error: pErr } = await supabaseClient
        .from('profiles')
        .select('user_id, name')
        .in('user_id', remainingIds)
      if (pErr) throw pErr
      if (requestId !== assignRequestRef.current) return
      // Defensive: only keep rows for the ids we actually requested.
      const remainingSet = new Set(remainingIds)
      setAssignOptions(
        (profiles ?? [])
          .filter((p): p is { user_id: string; name: string } => p.user_id != null && remainingSet.has(p.user_id))
          .map((p) => ({ value: p.user_id, label: p.name || 'Unknown member' })),
      )
    } catch (e) {
      if (requestId === assignRequestRef.current) message.error(groupsAdminErrorMessage(e))
    } finally {
      if (requestId === assignRequestRef.current) setAssignLoading(false)
    }
  }

  async function assignRep() {
    if (!assignFor || !assignTo) return
    setAssigning(true)
    try {
      const { error } = await supabaseClient.rpc('set_member_role', {
        p_group: assignFor.id,
        p_user: assignTo,
        p_role: 'organizer',
      })
      if (error) throw error
      message.success('Class rep assigned')
      setAssignFor(null)
      await load()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setAssigning(false)
    }
  }

  /** Demote to member. The rep stays in the Classroom; set_member_role's chapter branch is admin-only. */
  async function removeRep(row: ChapterRow, rep: Person) {
    try {
      const { error } = await supabaseClient.rpc('set_member_role', {
        p_group: row.id,
        p_user: rep.user_id,
        p_role: 'member',
      })
      if (error) throw error
      message.success(`${rep.name} is no longer a class rep`)
      await load()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    }
  }

  // ── who can open Classrooms ──
  // `expectedMode` (default undefined — no guard) passes straight through to
  // save()'s stale-write guard (A5, useClassroomSettings.ts): a no-dialog
  // write issued BECAUSE the caller believes the server is still in that
  // mode (onYearsChange's staging branch below) must not silently widen
  // anything if a second admin already changed it from another tab.
  async function write(
    patch: Partial<ClassroomSettings>,
    successText = 'Saved. The server applies it at once; open apps catch up on their next refresh.',
    expectedMode?: ClassroomMode,
  ) {
    setSaving(true)
    try {
      await save(patch, expectedMode)
      message.success(successText)
    } catch (e) {
      message.error(`Couldn’t save: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSaving(false)
    }
  }

  function onModeChange(next: ClassroomMode) {
    if (!settings || next === settings.mode) return
    // Switching TO Pilot always stores exactly the classes named in the
    // confirm (or staged already) — on the widening path AND the narrowing
    // one (e.g. Everyone → Pilot), never just the bare mode.
    const patch: Partial<ClassroomSettings> = next === 'pilot' ? { mode: 'pilot', pilot_years: settings.pilot_years } : { mode: next }
    // Narrowing is the brake: one tap, no dialog.
    if (!isWidening(settings.mode, next)) {
      void write(patch)
      return
    }
    const copy = modeConfirm(
      next,
      settings.pilot_years,
      yearsWithoutClassroom(settings.pilot_years, chapterStatus),
      yearsInactive(settings.pilot_years, chapterStatus),
      block,
      upstream.groupsAudience,
    )
    modal.confirm({
      title: copy.title,
      content: copy.content,
      okText: copy.okText,
      cancelText: 'Cancel',
      onOk: () => write(patch),
    })
  }

  function onYearsChange(values: string[]) {
    if (!settings) return
    const next = normalizeYears(values)
    // Outside Pilot, staging pilot years never opens anything — write with no
    // dialog. `settings.mode` is the CLIENT's belief, and classroom_settings
    // has no Realtime (A4), so a second admin could have already switched the
    // server to Pilot from another tab: pass it as `expectedMode` (A5) so a
    // drifted mode refuses the write instead of silently opening these years.
    if (settings.mode !== 'pilot') {
      void write({ pilot_years: next }, 'Saved. These classes open when you switch to Pilot.', settings.mode)
      return
    }
    const added = addedYears(settings.pilot_years, next)
    // Removing a year narrows: one tap.
    if (added.length === 0) {
      void write({ pilot_years: next })
      return
    }
    const copy = yearsConfirm(added, yearsWithoutClassroom(added, chapterStatus), yearsInactive(added, chapterStatus), block)
    modal.confirm({
      title: copy.title,
      content: copy.content,
      okText: copy.okText,
      cancelText: 'Cancel',
      onOk: () => write({ pilot_years: next }),
    })
  }

  // ── Memories ──
  async function writeMemories(on: boolean) {
    setSavingMemories(true)
    try {
      await setMemories(on)
      message.success(on ? 'Memories are on.' : 'Memories are off.')
    } catch (e) {
      message.error(`Couldn’t change Memories: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSavingMemories(false)
    }
  }

  function onMemoriesChange(on: boolean) {
    if (!on) {
      void writeMemories(false)
      return
    }
    modal.confirm({
      title: 'Turn on Memories?',
      content: 'Members of every open Classroom can add photos, react and comment right away.',
      okText: 'Turn on',
      cancelText: 'Cancel',
      onOk: () => writeMemories(true),
    })
  }

  // While the upstream switches can't be read, "Open to members" would be a
  // guess dressed as a count — show "—" instead.
  const openCount =
    settings && block !== 'unknown'
      ? rows.filter((r) => classroomOpenState(settings, r.class_year, r.status, upstream) === 'open').length
      : null
  const stats: Stat[] = [
    { label: 'Classrooms', value: rows.length.toLocaleString(), icon: GraduationCap, tone: 'jaguar' },
    {
      label: 'Open to members',
      value: openCount === null ? '—' : openCount.toLocaleString(),
      icon: Sparkles,
      tone: openCount ? 'green' : 'neutral',
    },
    {
      label: 'Total classmates',
      value: rows.reduce((sum, r) => sum + r.member_count, 0).toLocaleString(),
      icon: UsersRound,
      tone: 'gold',
    },
  ]

  // Shown above the mode controls whenever Group Settings itself keeps every
  // Classroom closed, or its switches can't be read. Computed here, OUTSIDE
  // the settingsLoading/unreadable ternary below, so that ternary's three
  // branches keep the shape they had before this alert existed instead of
  // nesting one level deeper inside a wrapping fragment.
  const upstreamAlert =
    block === 'groups_off' || block === 'chapters_off' ? (
      <Alert
        type="error"
        showIcon
        data-testid="classrooms-upstream-closed"
        message="Every Classroom is closed, whatever you choose here"
        description={
          <>
            {block === 'groups_off'
              ? 'Groups is off in Group Settings (Who can use Groups: Off). That closes every Classroom for everyone, admins included.'
              : 'Class chapters is off in Group Settings. That closes every Classroom for everyone, admins included.'}{' '}
            <Link to="/groups_settings">Open Group Settings</Link>
          </>
        }
        style={{ marginBottom: 12 }}
      />
    ) : block === 'unknown' ? (
      <Alert
        type="warning"
        showIcon
        data-testid="classrooms-upstream-unknown"
        message="Couldn’t read the Groups switches"
        description={
          <>
            The Open column shows “—” until Groups and Class chapters can be read.{' '}
            <Link to="/groups_settings">Open Group Settings</Link>
          </>
        }
        style={{ marginBottom: 12 }}
      />
    ) : null

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Groups',
        description:
          'One Classroom per class year (the “Class of YYYY” group), kept in sync automatically as members’ class years are set. Choose who can open Classrooms, switch Memories on or off, and assign or remove class reps. Click a class to see its roster.',
        actions:
          upstream.chaptersOn === false ? (
            <Tooltip title="Class chapters is off in Group Settings, so there is nothing to backfill.">
              <span>
                <Button icon={<RefreshCw size={14} />} loading={backfilling} onClick={backfill} data-testid="chapters-backfill" disabled>
                  Backfill chapters
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button icon={<RefreshCw size={14} />} loading={backfilling} onClick={backfill} data-testid="chapters-backfill">
              Backfill chapters
            </Button>
          ),
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard
        title="Who can open Classrooms"
        sub="Applies on the server at once; apps catch up on their next refresh"
        style={{ ...CARD, marginBottom: 16 }}
      >
        <div data-testid="classrooms-rollout">
          {!settingsLoading && upstreamAlert}
          {settingsLoading ? (
            <div style={muted}>Reading who can open Classrooms…</div>
          ) : unreadable || !settings ? (
            <div>
              <div style={{ marginBottom: 6 }}>
                <Pill tone="red">Unreadable</Pill>
              </div>
              <div style={muted}>
                Couldn’t read who can open Classrooms, so this page won’t guess. Members may or may not see
                Classrooms right now. Try again before changing anything.
              </div>
              <Button size="small" style={{ marginTop: 10 }} onClick={() => void refetchSettings()}>
                Try again
              </Button>
            </div>
          ) : (
            <>
              <Segmented<ClassroomMode>
                data-testid="classrooms-mode"
                block
                value={settings.mode}
                disabled={saving}
                options={CLASSROOM_MODES.map((m) => ({ label: MODE_LABELS[m], value: m }))}
                onChange={onModeChange}
              />
              <div style={{ ...muted, marginTop: 10 }}>{MODE_HELP[settings.mode]}</div>
              <div style={{ marginTop: 16 }}>
                <div style={fieldLabel}>Pilot class years</div>
                <Select
                  data-testid="classrooms-pilot-years"
                  mode="multiple"
                  aria-label="Pilot class years"
                  style={{ width: '100%' }}
                  value={settings.pilot_years}
                  options={CLASS_YEAR_OPTIONS.map((o) => ({ value: o.value, label: pilotYearLabel(o.value, chapterStatus) }))}
                  disabled={saving}
                  onChange={onYearsChange}
                  placeholder="Add a class year"
                />
                <div style={{ ...muted, marginTop: 4 }}>
                  {settings.mode === 'pilot'
                    ? 'Adding a class asks first; removing one is instant.'
                    : 'Classes that open when you switch to Pilot. Changing them now opens nothing.'}
                </div>
              </div>
              <div data-testid="classrooms-open-now" style={{ ...strong, marginTop: 14 }}>
                {openNowText(settings, upstream)}
              </div>
              <div data-testid="classrooms-prereq-note" style={{ ...muted, marginTop: 4 }}>
                Everyone, admins included, needs Groups and Class chapters on in{' '}
                <Link to="/groups_settings">Group Settings</Link>. With both on, admins see every Classroom in
                every mode.
              </div>
              {changedAt ? (
                // The touch trigger (classroom_settings_touch, migration
                // 20261016120000_classrooms_gate.sql) sets updated_by :=
                // coalesce(auth.uid(), old.updated_by) on every write, so a
                // non-null value names only the last DASHBOARD writer — a
                // null value proves the row was never written from here, not
                // that nobody has changed it.
                <div data-testid="classrooms-last-changed" style={{ ...muted, marginTop: 4 }}>
                  {changedBy ? `Last changed ${fmt(changedAt)}. Last dashboard change: ${changedBy}.` : `Last changed ${fmt(changedAt)}, outside the dashboard.`}
                </div>
              ) : null}
            </>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Memories" sub="Changes reach the app within seconds" style={{ ...CARD, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
            <div style={{ marginTop: 2, flexShrink: 0 }}>
              <ImageIcon size={18} color={brand.gold} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <span style={{ ...strong, fontSize: 14 }}>Memories in Classrooms</span>
                {memoriesEnabled !== null ? (
                  <Pill tone={memoriesEnabled ? 'green' : 'neutral'}>{memoriesEnabled ? 'On' : 'Off'}</Pill>
                ) : null}
              </div>
              <div data-testid="classrooms-memories-help" style={muted}>
                {settingsLoading
                  ? 'Reading the Memories switch…'
                  : memoriesEnabled === null
                    ? `The ${MEMORIES_FLAG_KEY} flag can’t be read, so Memories can’t be switched here.`
                    : memoriesEnabled
                      ? 'Classmates can add photos, react and comment. Switch off to stop new memories, reactions and comments and hide the Memories tab — memories already posted stay in the Gallery and keep showing as each Classroom’s cover on Home.'
                      : 'Hidden: Classrooms open on About, and nobody can add, react or comment. Memories already posted stay in the Gallery and as each Classroom’s cover on Home.'}
              </div>
            </div>
          </div>
          <div style={{ flexShrink: 0 }}>
            <Switch
              data-testid="classrooms-memories-switch"
              aria-label="Memories in Classrooms"
              checked={memoriesEnabled === true}
              disabled={memoriesEnabled === null}
              loading={savingMemories}
              onChange={onMemoriesChange}
              style={{ background: memoriesEnabled ? brand.gold : undefined }}
            />
          </div>
        </div>
      </SectionCard>

      <TableSurface>
        <Table<ChapterRow> dataSource={rows} rowKey="id" loading={loading} pagination={rows.length > 50 ? { pageSize: 50 } : false}>
          <Table.Column<ChapterRow>
            dataIndex="class_year"
            title="Class year"
            render={(_: string, r) => (
              <Link to={`/groups/show/${r.id}`} title="Open this Classroom’s roster">
                <Pill tone="jaguar">{r.name}</Pill>
              </Link>
            )}
          />
          <Table.Column<ChapterRow> dataIndex="member_count" title="Members" />
          <Table.Column<ChapterRow> dataIndex="directory_count" title="Directory count" />
          <Table.Column<ChapterRow>
            title="Difference"
            dataIndex="diff"
            render={(_: unknown, r) => {
              const diff = r.member_count - r.directory_count
              const big = Math.abs(diff) > 1
              return <span style={{ color: big ? '#a4452f' : brand.sub, fontWeight: big ? 700 : 500 }}>{diff}</span>
            }}
          />
          <Table.Column<ChapterRow>
            title="Open"
            dataIndex="open"
            render={(_: unknown, r) => {
              const state = classroomOpenState(settings, r.class_year, r.status, upstream)
              if (state === 'unknown') return <span style={{ color: brand.sub }}>—</span>
              if (state === 'inactive') return <Pill tone="red">Not active</Pill>
              return state === 'open' ? <Pill tone="green">Open</Pill> : <Pill tone="neutral">Closed</Pill>
            }}
          />
          <Table.Column<ChapterRow>
            title="Memories"
            dataIndex="memory_count"
            render={(_: unknown, r) =>
              r.extras_known ? r.memory_count.toLocaleString() : <span style={{ color: brand.sub }}>—</span>
            }
          />
          <Table.Column<ChapterRow>
            title="Founder"
            dataIndex="founder"
            render={(_: unknown, r) =>
              !r.extras_known ? (
                <span style={{ color: brand.sub }}>—</span>
              ) : r.founder ? (
                r.founder.name
              ) : (
                <span style={{ color: brand.sub }}>None yet</span>
              )
            }
          />
          <Table.Column<ChapterRow>
            dataIndex="reps"
            title="Class reps"
            render={(_: unknown, row) =>
              row.reps.length ? (
                <Space size={6} wrap>
                  {row.reps.map((rep) => (
                    <span key={rep.user_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <Pill tone="gold">{rep.name}</Pill>
                      <Popconfirm
                        title={`Remove ${rep.name} as class rep?`}
                        description="They stay in the Classroom as a classmate."
                        okText="Remove"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => removeRep(row, rep)}
                      >
                        <Button
                          type="text"
                          size="small"
                          aria-label={`Remove ${rep.name} as class rep`}
                          data-testid={`classrooms-remove-rep-${row.id}-${rep.user_id}`}
                          icon={<XCircle size={13} color={brand.sub} />}
                        />
                      </Popconfirm>
                    </span>
                  ))}
                </Space>
              ) : (
                <span style={{ color: brand.sub }}>None assigned</span>
              )
            }
          />
          <Table.Column<ChapterRow>
            title="Actions"
            dataIndex="actions"
            render={(_: unknown, r) => {
              // Reps get Stream channel_moderator on class-<yyyy> as soon as they're
              // assigned (_shared/groupChapterRoles.ts) — assigning one before the
              // class is open would hand out live-chat moderation for a Classroom
              // members can't even see yet. Removing a rep has no such side effect,
              // so it stays enabled in every mode (spec §2c). Gated on
              // classroomOpenState (mode AND upstream AND status), not the bare
              // mode check isClassroomOpen used to use — the server's own
              // classroom_rep_active() (20261018120500_groups_audience_fixes_round2.sql)
              // ANDs groups_enabled and groups_class_chapters_enabled into
              // classroom_open() before letting set_member_role appoint a rep, so a
              // button enabled only by mode (Open column already says "Closed", or
              // upstream is merely 'unknown') would just raise `classroom_closed`.
              const state = classroomOpenState(settings, r.class_year, r.status, upstream)
              const open = state === 'open'
              const button = (
                <Button size="small" disabled={!open} onClick={() => openAssign(r)}>
                  Assign class rep
                </Button>
              )
              if (open) return button
              const tooltipTitle =
                block === 'groups_off' || block === 'chapters_off'
                  ? 'Reps can be assigned once Groups and Class chapters are both on in Group Settings, and this Classroom is open to members (Pilot or Everyone).'
                  : 'Reps can be assigned once this Classroom is open to members (Pilot or Everyone).'
              return (
                <Tooltip title={tooltipTitle}>
                  <span>{button}</span>
                </Tooltip>
              )
            }}
          />
        </Table>
      </TableSurface>

      <Modal
        title={assignFor ? `Assign a class rep — Class of ${assignFor.class_year}` : 'Assign a class rep'}
        open={Boolean(assignFor)}
        onCancel={() => setAssignFor(null)}
        onOk={assignRep}
        okText="Assign"
        confirmLoading={assigning}
        okButtonProps={{ disabled: !assignTo }}
      >
        <Form layout="vertical">
          <Form.Item label="Member" help="Limited to this chapter’s active members.">
            <Select
              value={assignTo}
              onChange={setAssignTo}
              options={assignOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Select a member"
              loading={assignLoading}
              notFoundContent={assignLoading ? 'Loading members…' : assignEmpty}
            />
          </Form.Item>
        </Form>
      </Modal>
    </ListPage>
  )
}
